import { useRef } from "react";
import { Building2, CarFront } from "lucide-react";
import { cn } from "@ridereserv/shared/utils";
import type { Audience } from "./AudienceValueSection";

/** Toggle option order + labels. Fixes the tab order. */
const OPTIONS: readonly { value: Audience; label: string }[] = [
  { value: "companies", label: "For operators" },
  { value: "drivers", label: "For drivers" },
] as const;

const ICONS: Record<Audience, typeof Building2> = {
  companies: Building2,
  drivers: CarFront,
};

interface AudienceToggleProps {
  /** The currently-selected audience. */
  value: Audience;
  /** Called when the visitor picks a different audience. */
  onChange: (next: Audience) => void;
  /**
   * Accessible label for the control. Defaults to a generic label; the page
   * passes the id of a visible heading via `aria-labelledby` instead when one
   * exists.
   */
  "aria-label"?: string;
  /** Id of an external visible label (takes precedence over `aria-label`). */
  "aria-labelledby"?: string;
  className?: string;
}

/**
 * The page-level audience segmented control — a single, prominent toggle that
 * switches the merged value section, the pricing section, and the FAQ between
 * the company and driver funnels at once. Consolidating audience selection
 * behind ONE control (instead of separate per-section filters) is the whole
 * point: the visitor declares who they are once and the value area follows.
 *
 * Implemented as an accessible roving-tabindex tablist — arrow keys move focus
 * and selection together, the selected tab is the only tab stop, and each tab
 * keeps the 44px coarse tap-target floor while densifying on fine pointers.
 * Controlled component: the parent owns the audience state.
 * @param root0 - Component props
 * @param root0.value - The selected audience
 * @param root0.onChange - Selection handler
 * @param root0.className - Optional extra classes on the tablist wrapper
 * @param root0."aria-label" - Accessible label (fallback when no labelledby)
 * @param root0."aria-labelledby" - Id of an external visible label
 * @returns The audience toggle
 */
export function AudienceToggle({
  value,
  onChange,
  className,
  "aria-label": ariaLabel = "Choose your audience",
  "aria-labelledby": ariaLabelledby,
}: AudienceToggleProps) {
  // One ref per radio so arrow-key selection can move DOM focus onto the
  // newly-checked radio (roving-tabindex: focus follows selection).
  const radioRefs = useRef<Record<Audience, HTMLButtonElement | null>>({
    companies: null,
    drivers: null,
  });

  const select = (next: Audience) => {
    onChange(next);
    radioRefs.current[next]?.focus();
  };

  // A single audience that reconfigures three sibling sections is a
  // single-select choice, not a tab/panel widget — so this is a radiogroup
  // (role=radiogroup / role=radio + aria-checked), NOT a tablist. (A tablist
  // would imply an associated tabpanel referenced via aria-controls, which
  // these disjoint sections are not.) Roving tabindex + arrow keys still apply.
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabelledby ? undefined : ariaLabel}
      aria-labelledby={ariaLabelledby}
      className={cn(
        "inline-flex rounded-md border border-neutral-200 bg-surface p-1",
        className
      )}
    >
      {OPTIONS.map((option) => {
        const checked = option.value === value;
        const Icon = ICONS[option.value];
        return (
          <button
            key={option.value}
            ref={(node) => {
              radioRefs.current[option.value] = node;
            }}
            type="button"
            role="radio"
            aria-checked={checked}
            tabIndex={checked ? 0 : -1}
            onClick={() => {
              select(option.value);
            }}
            onKeyDown={(event) => {
              // Standard radiogroup keys: Right/Down → next, Left/Up → prev,
              // wrapping cyclically. Computed from OPTIONS (not a hardcoded
              // two-value flip) so it stays correct if the option set grows.
              const forward =
                event.key === "ArrowRight" || event.key === "ArrowDown";
              const backward =
                event.key === "ArrowLeft" || event.key === "ArrowUp";
              if (!forward && !backward) return;
              event.preventDefault();
              const index = OPTIONS.findIndex((o) => o.value === option.value);
              const delta = forward ? 1 : -1;
              const nextIndex =
                (index + delta + OPTIONS.length) % OPTIONS.length;
              const nextOption = OPTIONS[nextIndex];
              if (nextOption) select(nextOption.value);
            }}
            className={cn(
              "inline-flex min-h-11 items-center gap-2 rounded-sm px-4 text-sm font-medium transition-colors pointer-fine:min-h-9",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1 focus-visible:ring-offset-surface",
              checked
                ? "bg-primary-600 text-white"
                : "text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900"
            )}
          >
            <Icon
              className="h-4 w-4 shrink-0"
              strokeWidth={1.75}
              aria-hidden="true"
            />
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
